/**
 * useLandingSLKflowLandingMapDemoLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait LandingSLKflowLandingMapDemo
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, LandingSLKflowLandingMap, LandingSLKflowLandingMapDemoEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type LandingSLKflowLandingMapDemoState =
  | 'idle';

export type LandingSLKflowLandingMapDemoEvent =
  | 'INIT'
  | 'CONCEPT_PICKED';

type LandingSLKflowLandingMapDemoSlot = 'main';
interface LandingSLKflowLandingMapDemoInternalState {
  machineState: LandingSLKflowLandingMapDemoState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: LandingSLKflowLandingMapDemoEvent | null;
  slotWriter: Partial<Record<LandingSLKflowLandingMapDemoSlot, LandingSLKflowLandingMapDemoEvent | null>>;
  loading: boolean;
  pendingEvent: LandingSLKflowLandingMapDemoEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<LandingSLKflowLandingMap>;
}

type LandingSLKflowLandingMapDemoAction =
  | { type: 'EVENT_SUCCESS'; event: LandingSLKflowLandingMapDemoEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: LandingSLKflowLandingMapDemoSlot[]; renderClear?: LandingSLKflowLandingMapDemoSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: LandingSLKflowLandingMapDemoEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof LandingSLKflowLandingMap & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function landingSLKflowLandingMapDemoReducer(
  state: LandingSLKflowLandingMapDemoInternalState,
  action: LandingSLKflowLandingMapDemoAction
): LandingSLKflowLandingMapDemoInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as LandingSLKflowLandingMapDemoState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<LandingSLKflowLandingMap>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<LandingSLKflowLandingMap>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<LandingSLKflowLandingMap>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: LandingSLKflowLandingMapDemoInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface LandingSLKflowLandingMapDemoConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  mapView: { children: { children: Array<Array<string | string[] | { align: string; children: Array<{ color: string; content: string[]; type: string; variant: string } | { color: string; name: string; size: string; type: string }>; direction: string; gap: string; type: string } | { children: Array<{ align: string; children: Array<{ color: string; content: string; type: string; variant: string } | { content: string; type: string; variant: string; weight: string }>; direction: string; gap: string; type: string; wrap: boolean } | { color: string; content: string; type: string; variant: string }>; direction: string; gap: string; type: string }> | { "aria-label": string[]; description: string[]; draggable: boolean; edges: { source: string; target: string }[]; height: number; interactive: boolean; layout: string; linkDistance: number; nodeClickEvent: string; nodeSpacing: number; nodes: { description: string[]; group: string[]; id: string; label: string[]; size: number }[]; repulsion: number; selectedNodeId: string; showLabels: boolean; type: string } | { align: string; children: Array<{ align: string; children: Array<{ color: string; content: string[]; type: string; variant: string } | { color: string; name: string; size: string; type: string }>; direction: string; gap: string; type: string } | { children: Array<{ color: string; content: string[]; type: string; variant: string } | { content: string[]; type: string; variant: string; weight: string }>; direction: string; gap: string; type: string }>; direction: string; gap: string; justify: string; type: string; wrap: boolean }>; direction: string; gap: string; type: string }[]; type: string };
}

const DEFAULT_LANDING_SL_KFLOW_LANDING_MAP_DEMO_CONFIG = {
  mapView: { children: [{ children: [{ align: "center", children: [{ children: [{ color: "muted", content: ["i18n/t", "kflow-landing:map.goalLabel"], type: "typography", variant: "caption" }, { content: ["i18n/t", "kflow-landing:map.goal"], type: "typography", variant: "large", weight: "semibold" }], direction: "vertical", gap: "xs", type: "stack" }, { align: "center", children: [{ color: "success", name: "circle-check", size: "sm", type: "icon" }, { color: "muted", content: ["i18n/t", "kflow-landing:map.progress"], type: "typography", variant: "caption" }], direction: "horizontal", gap: "xs", type: "stack" }], direction: "horizontal", gap: "sm", justify: "between", type: "stack", wrap: true }, { "aria-label": ["i18n/t", "kflow-landing:map.label"], description: ["i18n/t", "kflow-landing:map.description"], draggable: true, edges: [{ source: "functions", target: "regression" }, { source: "vectors", target: "regression" }, { source: "derivatives", target: "gradient" }, { source: "regression", target: "loss" }, { source: "loss", target: "gradient" }, { source: "vectors", target: "neurons" }, { source: "gradient", target: "backprop" }, { source: "neurons", target: "backprop" }, { source: "backprop", target: "training" }], height: 360, interactive: true, layout: "force", linkDistance: 110, nodeClickEvent: "CONCEPT_PICKED", nodeSpacing: 40, nodes: [{ description: ["i18n/t", "kflow-landing:map.d.functions"], group: ["i18n/t", "kflow-landing:map.level1"], id: "functions", label: ["i18n/t", "kflow-landing:map.c.functions"], size: 9 }, { description: ["i18n/t", "kflow-landing:map.d.vectors"], group: ["i18n/t", "kflow-landing:map.level1"], id: "vectors", label: ["i18n/t", "kflow-landing:map.c.vectors"], size: 9 }, { description: ["i18n/t", "kflow-landing:map.d.derivatives"], group: ["i18n/t", "kflow-landing:map.level1"], id: "derivatives", label: ["i18n/t", "kflow-landing:map.c.derivatives"], size: 9 }, { description: ["i18n/t", "kflow-landing:map.d.regression"], group: ["i18n/t", "kflow-landing:map.level2"], id: "regression", label: ["i18n/t", "kflow-landing:map.c.regression"], size: 10 }, { description: ["i18n/t", "kflow-landing:map.d.loss"], group: ["i18n/t", "kflow-landing:map.level2"], id: "loss", label: ["i18n/t", "kflow-landing:map.c.loss"], size: 10 }, { description: ["i18n/t", "kflow-landing:map.d.gradient"], group: ["i18n/t", "kflow-landing:map.level2"], id: "gradient", label: ["i18n/t", "kflow-landing:map.c.gradient"], size: 11 }, { description: ["i18n/t", "kflow-landing:map.d.neurons"], group: ["i18n/t", "kflow-landing:map.level3"], id: "neurons", label: ["i18n/t", "kflow-landing:map.c.neurons"], size: 11 }, { description: ["i18n/t", "kflow-landing:map.d.backprop"], group: ["i18n/t", "kflow-landing:map.level3"], id: "backprop", label: ["i18n/t", "kflow-landing:map.c.backprop"], size: 13 }, { description: ["i18n/t", "kflow-landing:map.d.training"], group: ["i18n/t", "kflow-landing:map.level4"], id: "training", label: ["i18n/t", "kflow-landing:map.c.training"], size: 15 }], repulsion: 650, selectedNodeId: "@entity.selectedId", showLabels: true, type: "graph-canvas" }, ["if", ["==", "@entity.selectedLabel", ""], { align: "center", children: [{ color: "muted", name: "mouse-pointer-click", size: "sm", type: "icon" }, { color: "muted", content: ["i18n/t", "kflow-landing:map.hint"], type: "typography", variant: "small" }], direction: "horizontal", gap: "sm", type: "stack" }, { children: [{ align: "center", children: [{ content: "@entity.selectedLabel", type: "typography", variant: "body", weight: "semibold" }, { color: "muted", content: "@entity.selectedGroup", type: "typography", variant: "caption" }], direction: "horizontal", gap: "sm", type: "stack", wrap: true }, { color: "muted", content: "@entity.selectedDescription", type: "typography", variant: "small" }], direction: "vertical", gap: "xs", type: "stack" }]], direction: "vertical", gap: "md", type: "stack" }], type: "card" },
} as LandingSLKflowLandingMapDemoConfig;

export interface LandingSLKflowLandingMapDemoLogicReturn {
  state: LandingSLKflowLandingMapDemoState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: LandingSLKflowLandingMapDemoEvent | null;
  slotWriter: Partial<Record<LandingSLKflowLandingMapDemoSlot, LandingSLKflowLandingMapDemoEvent | null>>;
  loading: boolean;
  pendingEvent: LandingSLKflowLandingMapDemoEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<LandingSLKflowLandingMap>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof LandingSLKflowLandingMapDemoEventPayloadMap]: (payload?: LandingSLKflowLandingMapDemoEventPayloadMap[K]) => Promise<EventResponse> };
  config: LandingSLKflowLandingMapDemoConfig;
}

export function useLandingSLKflowLandingMapDemoLogic(config?: Partial<LandingSLKflowLandingMapDemoConfig>): LandingSLKflowLandingMapDemoLogicReturn {
  const mergedConfig: LandingSLKflowLandingMapDemoConfig = { ...DEFAULT_LANDING_SL_KFLOW_LANDING_MAP_DEMO_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/landing-sl-kflow-landing-map-demo', orbitalName: 'LandingSL', traitName: 'LandingSLKflowLandingMapDemo' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(landingSLKflowLandingMapDemoReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['LandingSLKflowLandingMap'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
        _fields['selectedId'] = "backprop";
        fields = { ...(fields ?? {}), 'selectedId': _fields['selectedId'] } as Record<string, EventPayloadValue | undefined>;
        _fields['selectedLabel'] = "";
        fields = { ...(fields ?? {}), 'selectedLabel': _fields['selectedLabel'] } as Record<string, EventPayloadValue | undefined>;
        _fields['selectedGroup'] = "";
        fields = { ...(fields ?? {}), 'selectedGroup': _fields['selectedGroup'] } as Record<string, EventPayloadValue | undefined>;
        _fields['selectedDescription'] = "";
        fields = { ...(fields ?? {}), 'selectedDescription': _fields['selectedDescription'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'CONCEPT_PICKED') {
      // idle → idle (on CONCEPT_PICKED)
        _fields['selectedId'] = payloadObj?.id;
        fields = { ...(fields ?? {}), 'selectedId': _fields['selectedId'] } as Record<string, EventPayloadValue | undefined>;
        _fields['selectedLabel'] = (payloadObj?.row as EventPayload | undefined)?.label;
        fields = { ...(fields ?? {}), 'selectedLabel': _fields['selectedLabel'] } as Record<string, EventPayloadValue | undefined>;
        _fields['selectedGroup'] = (payloadObj?.row as EventPayload | undefined)?.group;
        fields = { ...(fields ?? {}), 'selectedGroup': _fields['selectedGroup'] } as Record<string, EventPayloadValue | undefined>;
        _fields['selectedDescription'] = (payloadObj?.row as EventPayload | undefined)?.description;
        fields = { ...(fields ?? {}), 'selectedDescription': _fields['selectedDescription'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: LandingSLKflowLandingMapDemoSlot[]; clear: LandingSLKflowLandingMapDemoSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'CONCEPT_PICKED': return { write: ['main'] as LandingSLKflowLandingMapDemoSlot[], clear: [] as LandingSLKflowLandingMapDemoSlot[] };
          case 'INIT': return { write: ['main'] as LandingSLKflowLandingMapDemoSlot[], clear: [] as LandingSLKflowLandingMapDemoSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'LandingSLKflowLandingMap': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as LandingSLKflowLandingMapDemoEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'LandingSLKflowLandingMapDemo': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof LandingSLKflowLandingMapDemoEventPayloadMap]: (payload?: LandingSLKflowLandingMapDemoEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'CONCEPT_PICKED': (payload?: LandingSLKflowLandingMapDemoEventPayloadMap['CONCEPT_PICKED']) => dispatch('CONCEPT_PICKED', payload),
  };

  useUIEvents(enqueueEvent, 'LandingSL.LandingSLKflowLandingMapDemo', ['INIT', 'CONCEPT_PICKED'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('LandingSLKflowLandingMapDemo', (): TraitStateSnapshot => ({
      traitName: 'LandingSLKflowLandingMapDemo',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT', 'CONCEPT_PICKED'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
